import { useCallback, Fragment } from 'react';
import { BKJIcon } from '@awais4123/fillify-design-library';
import {
  PositionText,
  ShiftsCalendarListByDayModalShiftCardWrapper,
  ShiftDate,
  ShiftID,
  PendingSignUpText,
  ShiftSectionA,
  AvatarIconWrapper,
} from 'pages/ShiftsPage/DaySelectionModal/DaySelectionModal.styled';
import { StyledBKJIcon } from 'components/JaneGridSelect/JaneGridSelect.styled';
import { Flex } from 'components/Flex';
import { useTranslation } from 'hooks/useTranslation';
import { ShiftStatusBadge } from 'components/ShiftStatusBadge';
import { JaneAvatar } from 'components/BKJJaneAvatar';
import { JaneName } from 'pages/DashboardPage/JaneName';
import { parseVerboseDate } from 'utils';
import { DaySelectionModalShiftCardPropType } from 'pages/ShiftsPage/DaySelectionModal/DaySelectionModal.types';
import { useSelector } from 'react-redux';
import { getTranslatedResponse } from 'utils/getTranslatedResponse';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import {
  isMultiLocationCallOutFeatureEnabledSelector,
  isMultiLocationSchedulingFeatureEnabledSelector,
  isMultiLocationTieringFeatureEnabledSelector,
} from 'store/selectors/featureFlagSelectors';
import { getIsJaneCommunityMismatch } from 'utils/getIsJaneCommunityMismatch';
import { removePositionModalRoutingService } from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/RemovePositionModal.ui.context';
import { ShiftCardIcon } from 'components/ShiftCardSkeleton/ShiftCardSkeleton.styled';

export const Card = (props: DaySelectionModalShiftCardPropType): JSX.Element => {
  const {
    order: {
      id,
      status,
      position,
      startAt,
      endAt,
      mainJane,
      community,
      recurrence,
      acceptedBySharedTeamMember,
      orderSchedule,
    },
  } = props;
  const { t, i18n } = useTranslation();

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      removePositionModalRoutingService.open({ shiftId: id }, 'DetailView');
    },
    [id],
  );

  const currentlySelectedLocation = getCurrentlySelectedLocation();
  const { timeZone } = currentlySelectedLocation;

  const isMultiLocationCallOutFeatureEnabled = useSelector(
    isMultiLocationCallOutFeatureEnabledSelector(Number(currentlySelectedLocation?.value)),
  );
  const isMultiLocationSchedulingFeatureEnabled = useSelector(
    isMultiLocationSchedulingFeatureEnabledSelector(Number(currentlySelectedLocation?.value)),
  );
  const isMultiLocationTieringFeatureEnabled = useSelector(
    isMultiLocationTieringFeatureEnabledSelector(Number(currentlySelectedLocation?.value)),
  );

  const isSharedJanePrimaryCommunityDifferentFromSelectedLocation = getIsJaneCommunityMismatch({
    acceptedBySharedTeamMember: acceptedBySharedTeamMember ?? null,
    status,
    mainJane: mainJane ?? null,
    currentlySelectedLocation,
    isMultiLocationCallOutFeatureEnabled,
    isMultiLocationSchedulingFeatureEnabled,
    isMultiLocationTieringFeatureEnabled,
  });

  return (
    <Fragment>
      <ShiftsCalendarListByDayModalShiftCardWrapper key={id} onClick={handleClick}>
        <ShiftSectionA>
          <ShiftStatusBadge status={status.value}>
            {getTranslatedResponse(status.name, 'status', i18n, t)}
          </ShiftStatusBadge>
          <ShiftID>
            {t('dashboard:SHIFT_NUMBER')}
            {orderSchedule.id}
          </ShiftID>
        </ShiftSectionA>
        <Flex flexDirection="column">
          <Flex childMarginRight="8px">
            <AvatarIconWrapper>
              <JaneAvatar
                size={30}
                status={status.value}
                url={mainJane?.avatarUrls?.small}
                pendingSignup={false}
              />
              {mainJane?.pendingSignup && (
                <StyledBKJIcon iconName="PendingSignup" left="17" top="16" width="20" />
              )}
            </AvatarIconWrapper>
            <Flex alignItems="center">
              <JaneName mainJane={mainJane} status={status.name} />
              {isSharedJanePrimaryCommunityDifferentFromSelectedLocation && (
                <Fragment>
                  &nbsp; &nbsp;
                  <BKJIcon iconName="Group2" width={16} height={14} />
                </Fragment>
              )}
            </Flex>
            {mainJane && mainJane?.pendingSignup && (
              <PendingSignUpText>({t('common:PENDING_SIGNUP')})</PendingSignUpText>
            )}
          </Flex>
          <PositionText>{position.name}</PositionText>
          <Flex alignItems="center">
            {recurrence !== null && <ShiftCardIcon iconName="Recurring" fontSize={24} />}
            <ShiftDate noMargin>
              {parseVerboseDate([startAt, endAt], timeZone)}&nbsp;•&nbsp;
              {community.name}
            </ShiftDate>
          </Flex>
        </Flex>
      </ShiftsCalendarListByDayModalShiftCardWrapper>
    </Fragment>
  );
};
